import {
  type BoostTAEConfig,
  getBoostTAE,
  type IBoostTAE,
} from '@boost/widget-integration-tae/lib';
import { Global } from 'framework/core';

import { startSession } from '../../workers/workers-provider';

@Global()
export class AppService {
  get TAEApp(): IBoostTAE {
    return getBoostTAE();
  }

  get TAEAppConfig(): BoostTAEConfig {
    return this.TAEApp.config;
  }

  get themeSettings() {
    return this.TAEAppConfig.templateSettings.themeSettings;
  }

  get translation() {
    return this.TAEAppConfig.translation;
  }

  get logger() {
    return this.TAEApp?.logger || console;
  }
  get templateSettings() {
    return this.TAEAppConfig.templateSettings || {};
  }

  get filterSettings() {
    return {
      ...(this.TAEAppConfig.templateSettings.filterSettings || {}),
      swatch_settings: this.TAEAppConfig.filterSettings?.swatch_settings,
    };
  }

  get currentAppIntegration() {
    return this.TAEAppConfig.templateSettings?.currentAppIntegration || {};
  }

  blockByType(type: string) {
    const blocks = this.TAEApp.blocks;
    const block = Object.values(blocks).find((b) => b.blockType === type);
    return block || null;
  }

  splitGoogleFontStyle(fontStyle: string) {
    if (!fontStyle) {
      return {
        fontWeight: 100,
        fontStyle: 'normal',
      };
    }
    if (fontStyle === 'regular') {
      return {
        fontWeight: 400,
        fontStyle: 'normal',
      };
    }
    const fontWeight = fontStyle.match(/\d+/);
    const fontStyleValue = fontStyle.replace(/\d+/g, '').trim();
    return {
      fontWeight: fontWeight ? fontWeight[0] : 100,
      fontStyle: fontStyleValue || 'normal',
    };
  }

  async templateRender<Data>(template: string, data: Data) {
    const translations = this.TAEAppConfig.translation || {};

    const { html } = await startSession('liquid-renderer').send({
      template,
      data: {
        ...data,
        translations,
      },
    });

    return html;
  }

  formatCurrency = (
    money: number | string,
    options: {
      format?: string;
      withTrailingZeros?: boolean;
      showCentAsSuperscript?: boolean;
      removePriceDecimal?: boolean;
      removeDecimalPoint?: boolean;
      showCurrencyCodes?: boolean;
    } = {}
  ): string => {
    let { format: inputFormat } = options;

    const {
      withTrailingZeros,
      showCentAsSuperscript,
      removePriceDecimal,
      removeDecimalPoint,
      showCurrencyCodes,
    } = options;

    if (!inputFormat && showCurrencyCodes) {
      inputFormat = showCurrencyCodes
        ? this.TAEAppConfig.shop?.money_format_with_currency
        : this.TAEAppConfig.shop?.money_format;
    }

    // Get format from input or config
    let format = inputFormat || this.TAEAppConfig.shop?.money_format;

    if (format === 'money_with_currency') {
      format = this.TAEAppConfig.shop?.money_format_with_currency;
    }

    // Convert string money to number (remove decimal point)
    if (typeof money === 'string') {
      money = money.replace('.', '');
    }

    // Strip HTML from format (inline stripHtml logic)
    format = format?.toString()?.replace(/<.*?>/gi, '')?.replace(/&#36;/g, '$') || '${{amount}}';

    // Extract format type from format string (inline getTypeFormatMoney logic)
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const matches = format.match(placeholderRegex);
    const typeFormatMoney = matches && matches.length > 1 ? matches[1] : '';

    // Format money by type (inline formatMoneyByType logic)
    const formatWithDelimiters = (
      number: number,
      precision: number = 2,
      thousands: string = ',',
      decimal: string = '.'
    ): string => {
      if (number !== 0 && !number) return '';

      const fixedNumber = parseFloat(`${number}`).toFixed(precision);
      const parts = fixedNumber.toString().split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    };

    let moneyFormat: string;
    switch (typeFormatMoney) {
      case 'amount':
        moneyFormat = formatWithDelimiters(Number(money), 2);
        break;
      case 'amount_no_decimals':
        moneyFormat = formatWithDelimiters(Number(money), 0);
        break;
      case 'amount_with_comma_separator':
        moneyFormat = formatWithDelimiters(Number(money), 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        moneyFormat = formatWithDelimiters(Number(money), 0, '.', ',');
        break;
      case 'amount_with_space_separator_no_comma':
        moneyFormat = formatWithDelimiters(Number(money), 2);
        break;
      case 'amount_no_decimals_with_space_separator':
        moneyFormat = formatWithDelimiters(Number(money), 0, ' ', '.');
        break;
      default:
        moneyFormat = formatWithDelimiters(Number(money), 2);
    }

    // Remove trailing zeros (inline replaceDotCommaZero logic)
    if (!withTrailingZeros) {
      moneyFormat = moneyFormat.replace(/[.,]00$/, '');
    }

    // Remove decimal (inline removeDecimal logic)
    if (removePriceDecimal) {
      const delimiter = this.TAEAppConfig.generalSettings?.decimalDelimiter || '.';
      const reg = new RegExp('(\\' + delimiter + '\\d+)+', 'gi');
      moneyFormat = moneyFormat.replace(reg, '');
    }

    // Format money with cent as superscript (inline separateDecimalMoney and getDecimalPoint logic)
    if (showCentAsSuperscript) {
      // Get decimal point based on format type (inline getDecimalPoint logic)
      let decimalPoint: string | null;
      switch (typeFormatMoney) {
        case 'amount':
        case 'amount_with_space_separator_no_comma':
          decimalPoint = '.';
          break;
        case 'amount_with_comma_separator':
          decimalPoint = ',';
          break;
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
        case 'amount_no_decimals_with_space_separator':
        default:
          decimalPoint = null;
      }

      // Separate decimal money (inline separateDecimalMoney logic)
      if (decimalPoint) {
        const [ints, cents] = moneyFormat.split(decimalPoint);
        if (cents) {
          const centsDisplay = removeDecimalPoint ? cents : `${decimalPoint}${cents}`;
          moneyFormat = `${ints}<sup>${centsDisplay}</sup>`;
        }
      }
    }

    // Replace placeholder in format string with formatted money (inline compileMoneyWithConfigFormat logic)
    const result = format.replace(placeholderRegex, moneyFormat);

    return result;
  };

  generateUuid(): string {
    return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, () => {
      const r = (Math.random() * 16) | 0;
      return r.toString(16);
    });
  }

  /**
   * Formats price with shop-specific settings from TAE config
   * Wrapper around formatCurrency that applies theme settings automatically
   *
   * @param price - The price value in cents to format
   * @returns Formatted price string with currency symbol and shop-specific styling applied
   */
  formatPrice(price: number | string): string {
    const taeConfig = this.TAEAppConfig;

    const priceSettings =
      taeConfig.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};

    const showCentAsSuperscript = priceSettings.showCentAsSuperscript;
    const removePriceDecimal = Boolean(
      (priceSettings as Record<string, unknown>).removePriceDecimal
    );

    const formatString = taeConfig.shop.money_format || '${{amount}}';

    const formattedResult = this.formatCurrency(price, {
      format: formatString,
      showCentAsSuperscript: showCentAsSuperscript,
      removePriceDecimal: removePriceDecimal,
    });

    return formattedResult || '';
  }

  public detectDeviceByWidth(): string {
    const isMobileWidth = (): boolean => {
      return window.innerWidth < 576;
    };

    const isTabletPortraitMaxWidth = (): boolean => {
      return window.innerWidth < 991;
    };

    const devices: string[] = [];

    if (isMobileWidth()) {
      devices.push('mobile');
    }

    if (isTabletPortraitMaxWidth()) {
      devices.push('tablet_portrait_max');
    }

    return devices.join('|');
  }

  getTAEModule<M>(name: string) {
    return this.TAEApp.getModuleByName(name) as M | undefined;
  }
}
